import { useEffect, useState, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Search,
  Loader2,
  Check,
  X,
  ZoomIn,
  Trash2,
  RotateCcw,
  Mail,
  Phone,
  Car,
  Calendar,
  AlertCircle,
  ShieldCheck,
  ShieldAlert,
  FileText,
  Eye,
  Crown,
} from "lucide-react";
import { toast } from "sonner";

const STATE_FILTERS = [
  { value: "active", label: "Activos" },
  { value: "deleted", label: "Eliminados" },
  { value: "all", label: "Todos" },
];

const ROLE_FILTERS = [
  { value: "all", label: "Todos los roles" },
  { value: "guest", label: "Huéspedes" },
  { value: "host", label: "Anfitriones" },
  { value: "admin", label: "Admins" },
];

const ID_DOC_LABELS = {
  government_id_front: "INE / Pasaporte (frente)",
  government_id_back: "INE / Pasaporte (reverso)",
  driver_license_front: "Licencia (frente)",
  driver_license_back: "Licencia (reverso)",
  selfie: "Selfie con identificación",
};
const ID_DOC_KEYS = Object.keys(ID_DOC_LABELS);

const STATUS_CLASS = {
  approved: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  rejected: "bg-red-100 text-red-700",
  needs_info: "bg-blue-100 text-blue-700",
  not_started: "bg-neutral-100 text-neutral-700",
  not_uploaded: "bg-neutral-100 text-neutral-500",
};

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function StatusBadge({ status, size = "sm" }) {
  const cls = STATUS_CLASS[status] || STATUS_CLASS.not_started;
  return (
    <Badge className={`${cls} ${size === "xs" ? "text-[10px]" : ""} capitalize`}>
      {status === "not_uploaded" ? "Sin subir" : status?.replace("_", " ") || "—"}
    </Badge>
  );
}

export default function AdminAccountsTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [state, setState] = useState("active");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selected, setSelected] = useState(null); // { user, vehicles, recent_bookings }
  const [detailLoading, setDetailLoading] = useState(false);
  const [zoomDoc, setZoomDoc] = useState(null);

  // Reject reason dialog state
  const [rejectFor, setRejectFor] = useState(null); // { docKey, status }
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSaving, setRejectSaving] = useState(false);

  // Hard-delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { state, limit: 100 };
      if (role !== "all") params.role = role;
      if (statusFilter !== "all") params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Error al cargar cuentas");
    } finally {
      setLoading(false);
    }
  }, [q, role, state, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchUsers, 200); // debounce search
    return () => clearTimeout(t);
  }, [fetchUsers]);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setSelected(data);
    } catch (e) {
      toast.error("Error al cargar usuario");
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshSelected = async () => {
    if (!selected?.user?.user_id) return;
    await openDetail(selected.user.user_id);
    fetchUsers();
  };

  const handleApproveDoc = async (docKey) => {
    if (!selected?.user) return;
    try {
      await api.put(`/admin/users/${selected.user.user_id}/verification`, {
        doc: docKey,
        status: "approved",
      });
      toast.success("Documento aprobado");
      await refreshSelected();
    } catch (e) {
      toast.error("Error al actualizar documento");
    }
  };

  const handleHostStatus = async (status, reason, docKey = null) => {
    if (!selected?.user) return;
    try {
      const body = { status };
      if (reason) body.reason = reason;
      if (docKey) body.doc = docKey;
      await api.put(`/admin/users/${selected.user.user_id}/host-verification`, body);
      toast.success(
        status === "approved" ? "Documento aprobado"
        : status === "rejected" ? "Documento rechazado"
        : "Solicitud de info enviada"
      );
      await refreshSelected();
    } catch (e) {
      toast.error("Error al actualizar verificación de propietario");
    }
  };

  const handleRejectDoc = async () => {
    if (!selected?.user || !rejectFor) return;
    if (!rejectReason.trim()) {
      toast.error("Escribe un motivo para rechazar");
      return;
    }
    setRejectSaving(true);
    try {
      if (rejectFor.kind === "host") {
        await handleHostStatus(rejectFor.status, rejectReason.trim(), rejectFor.docKey || null);
      } else {
        await api.put(`/admin/users/${selected.user.user_id}/verification`, {
          doc: rejectFor.docKey,
          status: rejectFor.status,
          reason: rejectReason.trim(),
        });
        toast.success(rejectFor.status === "rejected" ? "Documento rechazado" : "Solicitud de info enviada");
        await refreshSelected();
      }
      setRejectFor(null);
      setRejectReason("");
    } catch (e) {
      toast.error("Error al rechazar documento");
    } finally {
      setRejectSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!selected?.user) return;
    try {
      await api.put(`/admin/users/${selected.user.user_id}/restore`);
      toast.success("Cuenta restaurada");
      await refreshSelected();
    } catch (e) {
      toast.error("Error al restaurar cuenta");
    }
  };

  const handleHardDelete = async () => {
    if (!selected?.user) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${selected.user.user_id}`);
      toast.success("Cuenta eliminada permanentemente");
      setConfirmDelete(false);
      setSelected(null);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar cuenta");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-accounts-tab">
      {/* Toolbar */}
      <div className="card-base">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="accounts-search-input"
              placeholder="Buscar por nombre, email o teléfono…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 h-10"
            />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-full md:w-44" data-testid="accounts-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_FILTERS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-full md:w-40" data-testid="accounts-state-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATE_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-44" data-testid="accounts-status-filter">
              <SelectValue placeholder="Verificación" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las verificaciones</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="approved">Aprobadas</SelectItem>
              <SelectItem value="rejected">Rechazadas</SelectItem>
              <SelectItem value="needs_info">Necesita info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-neutral-500 mt-2" data-testid="accounts-total">
          {loading ? "Cargando…" : `${total} cuentas encontradas`}
        </p>
      </div>

      {/* Users table */}
      <div className="card-base p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Anfitrión</TableHead>
                <TableHead className="text-right">Vehículos</TableHead>
                <TableHead className="text-right">Reservas</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-32 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const deletedDays = u.deleted_at ? daysSince(u.deleted_at) : null;
                return (
                  <TableRow
                    key={u.user_id}
                    className={`${u.deleted_at ? "bg-red-50/40" : ""} hover:bg-neutral-50 cursor-pointer`}
                    onClick={() => openDetail(u.user_id)}
                    data-testid={`account-row-${u.user_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-9 h-9">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="text-xs">
                            {(u.name || u.email || "U").slice(0, 1).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</div>
                          <div className="text-xs text-neutral-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">{u.role}</Badge>
                    </TableCell>
                    <TableCell><StatusBadge status={u.verification_status} /></TableCell>
                    <TableCell>
                      {u.role === "host" || u.role === "admin" ? (
                        <StatusBadge status={u.host_verification_status || "not_started"} />
                      ) : <span className="text-neutral-400 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{u.vehicle_count ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.booking_count ?? 0}</TableCell>
                    <TableCell>
                      {u.deleted_at ? (
                        <Badge className="bg-red-100 text-red-700 whitespace-nowrap" data-testid={`account-deleted-badge-${u.user_id}`}>
                          Eliminada · {Math.max(0, 30 - (deletedDays ?? 0))} d restantes
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">Activa</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); openDetail(u.user_id); }}
                        data-testid={`account-open-${u.user_id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-neutral-500 py-12">
                    No se encontraron cuentas con esos filtros.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent
          className="max-w-3xl"
          data-testid="account-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-terracotta" />
              Detalle de cuenta
            </DialogTitle>
            <DialogDescription>
              Gestión completa: documentos, anfitrión, restaurar o eliminar permanentemente.
            </DialogDescription>
          </DialogHeader>

          {detailLoading || !selected ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
            </div>
          ) : (
            <AccountDetailBody
              data={selected}
              onApprove={handleApproveDoc}
              onRejectClick={(docKey, status) => { setRejectFor({ docKey, status, kind: "id" }); setRejectReason(""); }}
              onApproveHost={(docKey) => handleHostStatus("approved", null, docKey || null)}
              onRejectHostClick={(status, docKey) => { setRejectFor({ kind: "host", status, docKey: docKey || null }); setRejectReason(""); }}
              onZoom={setZoomDoc}
              refreshSelected={refreshSelected}
            />
          )}

          {selected && (
            <DialogFooter className="gap-2 flex-wrap">
              {selected.user?.deleted_at ? (
                <Button
                  onClick={handleRestore}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="account-restore-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restaurar cuenta
                </Button>
              ) : null}
              {selected.user?.role !== "admin" && (
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="account-hard-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar permanentemente
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelected(null)}>
                Cerrar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Doc zoom modal */}
      <Dialog open={!!zoomDoc} onOpenChange={(o) => { if (!o) setZoomDoc(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{zoomDoc?.title}</DialogTitle>
          </DialogHeader>
          {zoomDoc?.url && (
            zoomDoc.url.startsWith("data:application/pdf") ? (
              <a href={zoomDoc.url} target="_blank" rel="noopener noreferrer" className="text-terracotta underline">
                Abrir PDF en nueva pestaña
              </a>
            ) : (
              <img src={zoomDoc.url} alt={zoomDoc.title} className="w-full max-h-[80vh] object-contain rounded-lg" />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) setRejectFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {rejectFor?.status === "needs_info" ? "Solicitar más información" : "Rechazar"}
            </DialogTitle>
            <DialogDescription>
              {rejectFor?.kind === "host"
                ? "Verificación de propietario"
                : rejectFor?.docKey ? ID_DOC_LABELS[rejectFor.docKey] : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="font-semibold">Motivo</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ej: La foto está borrosa, no se ve la fecha de vencimiento..."
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)} disabled={rejectSaving}>Cancelar</Button>
            <Button
              onClick={handleRejectDoc}
              disabled={rejectSaving || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-confirm-btn"
            >
              {rejectSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard-delete confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Eliminar cuenta permanentemente
            </DialogTitle>
            <DialogDescription>
              Esta acción borrará todos los datos del usuario, sus vehículos, mensajes y notificaciones.
              No es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancelar</Button>
            <Button
              onClick={handleHardDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="account-hard-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccountDetailBody({ data, onApprove, onRejectClick, onApproveHost, onRejectHostClick, onZoom, refreshSelected }) {
  const u = data.user;
  const docs = u.verification_docs || {};
  const hostStatus = u.host_verification_status || "not_started";
  return (
    <div className="space-y-5" data-testid="account-detail-body">
      {/* Profile header */}
      <div className="flex items-start gap-4">
        <Avatar className="w-14 h-14">
          <AvatarImage src={u.picture} />
          <AvatarFallback>{(u.name || u.email || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading font-semibold text-lg">{u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</h3>
            <Badge variant="outline" className="capitalize">{u.role}</Badge>
            {u.deleted_at && (
              <Badge className="bg-red-100 text-red-700">
                Eliminada hace {daysSince(u.deleted_at) ?? "?"} d
              </Badge>
            )}
            <a
              href={`/u/${u.user_id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-public-profile-link"
              className="ml-auto text-xs font-semibold text-terracotta hover:underline"
            >
              Ver perfil público →
            </a>
          </div>
          <div className="text-sm text-neutral-600 flex flex-wrap gap-4 mt-1">
            <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {u.email}</span>
            {u.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {u.phone}</span>}
            {u.created_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Miembro desde {new Date(u.created_at).toLocaleDateString("es-MX", { year: "numeric", month: "short" })}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge className={STATUS_CLASS[u.verification_status] || STATUS_CLASS.not_started}>
              ID · {u.verification_status || "not_started"}
            </Badge>
            {(u.role === "host" || u.role === "admin") && (
              <Badge className={STATUS_CLASS[u.host_verification_status] || STATUS_CLASS.not_started}>
                Anfitrión · {u.host_verification_status || "not_started"}
              </Badge>
            )}
            {u.premium_eligibility?.eligible && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                Premium · {u.premium_eligibility.via}
              </Badge>
            )}
            {u.vip_active && (
              <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                VIP {u.vip_expires_at ? `· vence ${String(u.vip_expires_at).slice(0,10)}` : ""}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Premium / VIP admin controls */}
      <PremiumVipPanel u={u} onRefresh={refreshSelected} />

      {/* Verification documents */}
      <div>
        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-terracotta" /> Documentos de identidad
        </h4>
        {u.verification_submitted_at && (
          <p className="text-xs text-amber-700 mb-3">
            Enviados a revisión el{" "}
            {new Date(u.verification_submitted_at).toLocaleString("es-MX")}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ID_DOC_KEYS.map((key) => {
            const d = docs[key];
            const status = d?.status || "not_uploaded";
            return (
              <div
                key={key}
                className="border rounded-xl p-3 bg-white"
                data-testid={`detail-doc-${key}`}
              >
                <div className="flex items-center justify-between mb-2 gap-2">
                  <p className="text-xs font-semibold flex-1 min-w-0 truncate">{ID_DOC_LABELS[key]}</p>
                  <StatusBadge status={status} size="xs" />
                </div>
                {d?.url ? (
                  d.url.startsWith("data:application/pdf") ? (
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-neutral-50 border rounded-lg p-2 mb-2 text-xs"
                    >
                      <FileText className="w-4 h-4" /> Ver PDF
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onZoom({ url: d.url, title: ID_DOC_LABELS[key] })}
                      className="block w-full mb-2 relative group"
                      data-testid={`detail-doc-zoom-${key}`}
                    >
                      <img src={d.url} alt={ID_DOC_LABELS[key]} className="w-full h-28 object-cover rounded-lg border" />
                      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition rounded-lg flex items-center justify-center">
                        <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
                      </span>
                    </button>
                  )
                ) : (
                  <div className="text-xs text-neutral-400 italic h-28 flex items-center justify-center border rounded-lg">
                    Sin subir
                  </div>
                )}
                {d?.rejection_reason && (
                  <p className="text-[11px] text-red-700 mb-2"><strong>Motivo:</strong> {d.rejection_reason}</p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 h-7 px-2 text-xs"
                    disabled={!d?.url || status === "approved"}
                    onClick={() => onApprove(key)}
                    data-testid={`detail-doc-approve-${key}`}
                  >
                    <Check className="w-3 h-3 mr-1" /> Aprobar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 h-7 px-2 text-xs"
                    disabled={!d?.url}
                    onClick={() => onRejectClick(key, "rejected")}
                    data-testid={`detail-doc-reject-${key}`}
                  >
                    <X className="w-3 h-3 mr-1" /> Rechazar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 h-7 px-2 text-xs"
                    disabled={!d?.url}
                    onClick={() => onRejectClick(key, "needs_info")}
                    data-testid={`detail-doc-needsinfo-${key}`}
                  >
                    <AlertCircle className="w-3 h-3 mr-1" /> Más info
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Host docs */}
      {(u.role === "host" || u.role === "admin") && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-terracotta" /> Documentos de propietario
            </h4>
            <StatusBadge status={hostStatus} size="xs" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: "proof_of_address", label: "Comprobante de domicilio" },
              { key: "vehicle_ownership_doc", label: "Factura / Carta factura" },
            ].map((it) => {
              const hostDocs = u.host_verification_docs || {};
              const docState = hostDocs[it.key] || {};
              const docStatus = u[it.key] ? (docState.status || "pending") : "not_uploaded";
              const docUrl = u[it.key];
              return (
                <div key={it.key} className="border rounded-xl p-3 bg-white" data-testid={`detail-host-doc-${it.key}`}>
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-xs font-semibold flex-1 min-w-0 truncate">{it.label}</p>
                    <StatusBadge status={docStatus} size="xs" />
                  </div>
                  {docUrl ? (
                    docUrl.startsWith("data:application/pdf") ? (
                      <a href={docUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-neutral-50 border rounded-lg p-2 mb-2 text-xs">
                        <FileText className="w-4 h-4" /> Ver PDF
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onZoom({ url: docUrl, title: it.label })}
                        className="block w-full mb-2 relative group"
                        data-testid={`detail-host-zoom-${it.key}`}
                      >
                        <img src={docUrl} alt={it.label} className="w-full h-28 object-cover rounded-lg border" />
                        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition rounded-lg flex items-center justify-center">
                          <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
                        </span>
                      </button>
                    )
                  ) : (
                    <div className="text-xs text-neutral-400 italic h-28 flex items-center justify-center border rounded-lg mb-2">
                      Sin subir
                    </div>
                  )}
                  {docState?.rejection_reason && (
                    <p className="text-[11px] text-red-700 mb-2"><strong>Motivo:</strong> {docState.rejection_reason}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 h-7 px-2 text-xs"
                      disabled={!docUrl || docStatus === "approved"}
                      onClick={() => onApproveHost(it.key)}
                      data-testid={`detail-host-approve-${it.key}`}
                    >
                      <Check className="w-3 h-3 mr-1" /> Aprobar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 h-7 px-2 text-xs"
                      disabled={!docUrl}
                      onClick={() => onRejectHostClick("rejected", it.key)}
                      data-testid={`detail-host-reject-${it.key}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Rechazar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 h-7 px-2 text-xs"
                      disabled={!docUrl}
                      onClick={() => onRejectHostClick("needs_info", it.key)}
                      data-testid={`detail-host-needsinfo-${it.key}`}
                    >
                      <AlertCircle className="w-3 h-3 mr-1" /> Más info
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vehicles */}
      {data.vehicles?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Car className="w-4 h-4 text-terracotta" /> Vehículos ({data.vehicles.length})
          </h4>
          <div className="space-y-1.5">
            {data.vehicles.map((v) => (
              <div key={v.vehicle_id} className="flex items-center justify-between bg-neutral-50 rounded-lg p-2 text-sm">
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{v.year} {v.make} {v.model}</span>
                  <span className="text-neutral-500 text-xs ml-2">{v.location_address}</span>
                </div>
                <Badge variant="outline" className="text-xs capitalize">{v.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent bookings */}
      {data.recent_bookings?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-terracotta" /> Reservas recientes
          </h4>
          <div className="space-y-1.5">
            {data.recent_bookings.slice(0, 5).map((b) => (
              <div key={b.booking_id} className="flex items-center justify-between bg-neutral-50 rounded-lg p-2 text-sm">
                <span className="text-xs text-neutral-700">{b.booking_id}</span>
                <Badge variant="outline" className="text-xs capitalize">{b.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function PremiumVipPanel({ u, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const elig = u.premium_eligibility || {};

  const runAction = async (action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.user_id}/premium-action`, { action, ...extra });
      toast.success("Acción aplicada");
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aplicar la acción");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-4 space-y-3" data-testid="admin-premium-panel">
      <h4 className="text-sm font-semibold flex items-center gap-2">
        <Crown className="w-4 h-4 text-amber-600" />
        Premium / Membresía VIP
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div>
          <p className="text-neutral-500">Elegibilidad</p>
          <p className={`font-semibold ${elig.eligible ? "text-emerald-700" : "text-amber-700"}`}>
            {elig.eligible ? "Desbloqueado" : "Bloqueado"}
          </p>
        </div>
        <div>
          <p className="text-neutral-500">Vía</p>
          <p className="font-semibold">{elig.via || "—"}</p>
        </div>
        <div>
          <p className="text-neutral-500">Viajes completados</p>
          <p className="font-semibold">
            {elig.completed_rental_count ?? 0} / 10
          </p>
        </div>
        <div>
          <p className="text-neutral-500">VIP vence</p>
          <p className="font-semibold">
            {u.vip_active && u.vip_expires_at ? String(u.vip_expires_at).slice(0, 10) : "—"}
          </p>
        </div>
      </div>
      {elig.reason && (
        <p className="text-xs text-amber-800 italic">Razón: {elig.reason}</p>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {!u.vip_active ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction("grant_vip")} data-testid="admin-grant-vip">
            Otorgar VIP (1 año)
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction("revoke_vip")} data-testid="admin-revoke-vip">
            Revocar VIP
          </Button>
        )}
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={extendDays}
            onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
            className="w-16 text-xs border border-neutral-300 rounded px-2 py-1"
            min="1"
            data-testid="admin-extend-vip-days"
          />
          <Button size="sm" variant="outline" disabled={busy || !extendDays} onClick={() => runAction("extend_vip", { extend_days: extendDays })} data-testid="admin-extend-vip">
            Extender días
          </Button>
        </div>
        {u.premium_admin_locked ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction("unlock_premium")} data-testid="admin-unlock-premium">
            Desbloquear Premium
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction("lock_premium")} data-testid="admin-lock-premium">
            Bloquear Premium
          </Button>
        )}
      </div>
    </div>
  );
}
